import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { cartItemsArrayPropTypes, userPropTypes } from 'helpers/propTypes';
import { isAbleToPurchase } from 'helpers/cart';
import { getLocalStorage } from 'helpers/storage';
import { CART_CHECKOUT_PICKUP_REQUESTED } from 'redux/constants/cart.constants';
import {
  checkout, submitCoupon, deleteCoupon, getProducts,
} from 'redux/actions/cart.actions';
import {
  cartItemIdsSelector, cartItemsSelector, cartSizeSelector, cartSubtotalSelector,
  cartSubtotalTextilSelector,
} from 'redux/selectors/cart.selector';
import { openSecureCheckoutModal } from 'redux/actions/modal.actions';
import { openPaymentMethodsModal } from 'redux/actions/modal.actions';

import Checkout from 'views/Checkout';
import PickupForm from 'components/forms/PickupForm';
import CheckoutTextil from 'views/Checkout/CheckoutTextil';
import ConfirmCart from 'components/commons/ConfirmCart';


const PickupContainer = (props) => {
  const {
    cartItems, cartSize, checkoutAction, submitCouponAction, items, loading, minimumPurchase,
    subtotal, user, coupon, deleteCouponAction, resetCouponField, openSecureCheckoutModalAction,
    showMPButton, getProductsAction, ids, transferInfo, client, subtotalTextil,
    cuotasMP, openPaymentMethodsModalAction, paymentMethods, hayAcuerdoConVendedor,
  } = props;
  const [couponText, setCouponText] = useState(null);
  const [emailComplete, setEmailComplete] = useState(false);
  const [emailCoupon, setEmailCoupon] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);

  useEffect(() => {
    if (coupon) deleteCouponAction();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (ids.length > 0 && !initialized) {
      getProductsAction(ids);
      setInitialized(true);
    }
    return () => getProductsAction([]);
    // eslint-disable-next-line
  }, [getProductsAction, JSON.stringify(ids)]);

  const onDeleteCoupon = () => {
    deleteCouponAction();
    resetCouponField('');
    setCouponText('');
  };

  const validationEmailCoupon = (email) => {
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setEmailComplete(false);
      onDeleteCoupon();
    } else {
      setEmailComplete(true);
      setEmailCoupon(email);
    }
  };

  const onConfirmCheckout = () => {
    if (!pendingCheckout) return;
    const { data, checkoutItems } = pendingCheckout;
    setModalOpen(false);
    checkoutAction(
      CART_CHECKOUT_PICKUP_REQUESTED,
      data,
      checkoutItems,
      user ? user.id : null,
    );
    deleteCouponAction();
    setPendingCheckout(null);
  };

  const onSubmitPickUpForm = (formData) => {
    const isMP = formData.card || formData.token;
    const methodCode = isMP ? 'MP' : formData.payment_method;

    const selectedMethod = paymentMethods.find(p => p.code === methodCode);
    const methodPercent = selectedMethod ? parseFloat(selectedMethod.percent) : 0;

    const base = getBaseForPayment(methodCode);
    const finalAmount = Math.round(base * (1 - methodPercent) * 100) / 100;

    const data = {
      ...formData,
      coupon: coupon?.id,
      payment_method: isMP ? 'MP' : formData.payment_method,
      transaction_amount: finalAmount,
      shipping_cost: 0,
    };

    const canCheckout = client?.design === 3
      ? isAbleToPurchase(subtotalTextil, minimumPurchase)
      : isAbleToPurchase(subtotal, minimumPurchase);

    if (canCheckout) {
      setPendingCheckout({ data, checkoutItems: cartItems });
      setModalOpen(true);
      window.scrollTo(0, 0);
    }
  };

  const onSubmitCoupon = () => {
    if (couponText && emailCoupon) {
      submitCouponAction(couponText, emailCoupon);
    }
  };

  const getBaseForPayment = (selectedMethodCode) => {
    const methodObj = paymentMethods.find(p => p.code === selectedMethodCode);
    const allowsAutoDiscount = methodObj ? methodObj.allow_discount : false;

    const discountFromStorage = getLocalStorage('discount');
    const currentSubtotal = client?.design === 3 ? subtotalTextil : subtotal;
    
    const baseValue = (allowsAutoDiscount && discountFromStorage !== null) 
        ? parseFloat(discountFromStorage) 
        : currentSubtotal;

    const couponAmount = coupon?.percentage
        ? (coupon.percentage / 100) * currentSubtotal
        : (coupon?.amount || 0);

    return Math.max(0, baseValue - couponAmount);
  };

  const onSecureCheckoutClick = (formData) => {
    const mpMethod = paymentMethods.find(p => p.code === 'MP' || p.name.toLowerCase().includes('mercado pago'));
    const discountPercent = mpMethod ? parseFloat(mpMethod.percent) : 0;
    const base = getBaseForPayment('MP');
    const finalAmount = Math.round(base * (1 - discountPercent) * 100) / 100;

    openSecureCheckoutModalAction({
      callback: onSubmitPickUpForm,
      email: formData.email_retire,
      formData,
      total: finalAmount,
    });
  };

  const handleOpenPaymentModal = () => {
    const finalPrice = subtotalTextil;
    openPaymentMethodsModalAction(cuotasMP, finalPrice);
  };

  const renderPickUpForm = () => (
    <PickupForm
      showMPButton={showMPButton}
      coupon={coupon}
      onSubmit={onSubmitPickUpForm}
      onSubmitCoupon={coupon ? onDeleteCoupon : onSubmitCoupon}
      onSecureCheckoutClick={onSecureCheckoutClick}
      couponText={couponText}
      setCouponText={setCouponText}
      emailComplete={emailComplete}
      setEmailComplete={setEmailComplete}
      setEmailCoupon={setEmailCoupon}
      transferInfo={transferInfo}
      validationEmailCoupon={validationEmailCoupon}
      user={user}
      hayAcuerdoConVendedor={hayAcuerdoConVendedor}
    />
  );

  const renderWithConfirmCart = (checkoutNode) => (
    <>
      {checkoutNode}
      <ConfirmCart
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={onConfirmCheckout}
      />
    </>
  );

  switch(client?.design) {
    case 3:
      return renderWithConfirmCart(
        <CheckoutTextil
          coupon={coupon}
          cartSize={cartSize}
          items={items}
          loading={loading}
          minimumPurchase={minimumPurchase}
          renderForm={renderPickUpForm}
          shippingText="Gratis"
          subtotal={subtotalTextil}
          url="/retiro-en-sucursal"
          urlText="Retiro en Sucursal"
          onOpenPaymentModal= {handleOpenPaymentModal}
          cuotasMP={cuotasMP}
          showMPButton={showMPButton}
          paymentMethods={paymentMethods}
        />,
      );
    default:
      return renderWithConfirmCart(
        <Checkout
          coupon={coupon}
          cartSize={cartSize}
          items={items}
          loading={loading}
          minimumPurchase={minimumPurchase}
          renderForm={renderPickUpForm}
          shippingText="Gratis"
          subtotal={subtotal}
          url="/retiro-en-sucursal"
          urlText="Retiro en Sucursal"
          onOpenPaymentModal= {handleOpenPaymentModal}
          cuotasMP={cuotasMP}
          showMPButton={showMPButton}
          client={client}
          paymentMethods={paymentMethods}
        />,
      );
  }
};

const mapStateToProps = (state) => ({
  coupon: state.cart.coupon,
  cartItems: state.cart.items,
  cartSize: cartSizeSelector(state),
  ids: cartItemIdsSelector(state),
  items: cartItemsSelector(state),
  loading: state.cart.loading,
  minimumPurchase: state.app.minimumPurchase,
  subtotal: cartSubtotalSelector(state),
  transferInfo: state.app.transferInfo,
  user: state.user.data,
  showMPButton: state.app.showMPButton,
  client: state.app.client,
  subtotalTextil: cartSubtotalTextilSelector(state),
  cuotasMP: state.app.cuotasMP,
  paymentMethods: state.app.paymentMethods,
  hayAcuerdoConVendedor: state.app.hayAcuerdoConVendedor,
});

const mapDispatchToProps = (dispatch) => ({
  checkoutAction: bindActionCreators(checkout, dispatch),
  submitCouponAction: bindActionCreators(submitCoupon, dispatch),
  deleteCouponAction: bindActionCreators(deleteCoupon, dispatch),
  resetCouponField: (value) => dispatch(change('pickup', 'coupon', value)),
  openSecureCheckoutModalAction: bindActionCreators(openSecureCheckoutModal, dispatch),
  getProductsAction: bindActionCreators(getProducts, dispatch),
  openPaymentMethodsModalAction: bindActionCreators(openPaymentMethodsModal, dispatch),
});

PickupContainer.defaultProps = {
  user: null,
  coupon: null,
};

PickupContainer.propTypes = {
  cartItems: cartItemsArrayPropTypes.isRequired,
  cartSize: PropTypes.number.isRequired,
  coupon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
    amount: PropTypes.number.isRequired,
  }),
  checkoutAction: PropTypes.func.isRequired,
  getProductsAction: PropTypes.func.isRequired,
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  submitCouponAction: PropTypes.func.isRequired,
  deleteCouponAction: PropTypes.func.isRequired,
  resetCouponField: PropTypes.func.isRequired,
  openSecureCheckoutModalAction: PropTypes.func.isRequired,
  items: cartItemsArrayPropTypes.isRequired,
  loading: PropTypes.bool.isRequired,
  minimumPurchase: PropTypes.number.isRequired,
  subtotal: PropTypes.number.isRequired,
  showMPButton: PropTypes.bool.isRequired,
  user: userPropTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(PickupContainer);
